import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'wouter';
import { queryParameters } from '@/functions/QueryParameters.js';
import { useCart } from '@/providers/CartProvider.jsx';
import DottedSpinner from "@/components/DottedSpinner.jsx";
import { actions } from "@/store/store.js";
import { dollarsFromCents } from "@/functions/Money.js";
import useCurrentAccount from "@/hooks/useCurrentAccount.js";

const ConfirmationPage = () => {
  const { clearCart } = useCart();
  const { currentAccount } = useCurrentAccount()
  const [confirmationCode, setConfirmationCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const params = queryParameters();
    if (params['forward_message'] === 'clearCart') clearCart();
    if (params['confirmation_code']) setConfirmationCode(params['confirmation_code']);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!confirmationCode) return;
    dispatch(actions.sales.loadOne({ id: confirmationCode }));
  }, [confirmationCode]);

  const sale = useSelector(
    ({ sales }) => Object.values(sales.records).find((sale) => sale.confirmation_code === confirmationCode)
  );

  const lineItems = useMemo(() => {
    if (!sale) return [];
    return sale.sales_line_items.map((lineItem) =>({
      id: lineItem.id,
      name: lineItem.sales_product?.sales_name,
      image: lineItem.sales_product?.sales_square,
      quantity: lineItem.quantity,
      price: lineItem.total_price_usd_cents,
      gpsShipping: lineItem.sales_product?.gps_shipping,
    }));
  }, [sale]);

  const requiresShipping = useMemo(() => {
    return lineItems.some((item) => item.gpsShipping);
  }, [lineItems]);

  const estimatedDelivery = useMemo(() => {
    return requiresShipping ? '7 to 10 business days' : 'Instant Delivery';
  }, [requiresShipping]);

  return (
    <div className="">
      <main className="mx-auto">
        <div className="p-4 text-center" style={{ border: '1px solid var(--color2)', borderRadius: "var(--spacing)" }}>
          {confirmationCode ? (
            <>
              <div className="d-flex justify-content-center mb-2">
                <div className="p-2">
                  <span className="fa fa-circle-check fa-4x"></span>
                </div>
              </div>

              <h1 className="text-large">Order Confirmed!</h1>
              <p className="mb-4">
                Your order has been successfully placed.
              </p>

              <div className="p-4 mb-3">
                <div className="row g-4">
                  <div className="col-12 col-md-6">
                    <p className="mb-2">Order Number</p>
                    <p className="text-large">{confirmationCode}</p>
                  </div>
                  <div className="col-12 col-md-6">
                    <p className="mb-2">Total Amount</p>
                    <p className="text-large">${dollarsFromCents(sale?.total_price_usd_cents)}</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="row">
                  <div className="mb-3 col-12 col-md-6">
                    <h3 className="text-large"><span className="fa fa-boxes-packing"></span> Estimated Delivery</h3>
                    <p className="">{estimatedDelivery}</p>
                  </div>

                  <div className="col-12 col-md-6">
                    <h3 className="text-large mb-2"><span className="fa fa-envelope"></span> Order Confirmation</h3>
                    <p>A confirmation email has been sent to the provided email address with order details.</p>
                  </div>
                </div>
              </div>

              <hr className="my-5"/>

              {lineItems.length > 0 && (
                <div className="mb-2">
                  <h3 className="text-large text-left">Order Items</h3>
                  <div className="py-4">
                    {lineItems.map(item => (
                      <div key={item.id} className="d-flex align-items-center text-left p-2 mb-2" style={{ border: '1px solid var(--color2)', borderRadius: "var(--spacing)" }}>
                        <div className="flex-1">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-100"
                          />
                        </div>
                        <div className="flex-3 ">
                          <p>{item.name}</p>
                          <p className="fine-print">Qty: {item.quantity}</p>
                        </div>
                        <p className="flex-1">${dollarsFromCents(item.price)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Link to="/" className="btn site-btn">
                Continue Shopping
              </Link>

              {currentAccount && (
                <a href={`${window.location.origin}${Routes.account_orders_path()}`} className="d-block mt-4">View recent orders</a>
              )}
            </>
          ) : (
            <>
              {loading ? (
                <DottedSpinner />
              ) : (
                <>
                  <h2>Order with that confirmation code not found.</h2>
                </>
              )}
              <Link to="/" className="btn site-btn">
                Continue Shopping
              </Link>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ConfirmationPage;
